
export type CareandPrompts = {
    "Appointment Action Suggestions": {
        input: {
            $$$HR: string;
            appointment_note: string;
            appointment_transcription: string;
            current_actions: string;
            extra_instructions: string;
        };
        output: {
            deltas: {
                change: "add" | "remove" | "update";
                action_id?: string | undefined;
                action: {
                    type: "prescription" | "referral" | "requisition" | "other_document";
                    description: string;
                    status: "ready" | "in-progress" | "pending-approval" | "done" | "rejected" | "error";
                    specialty?: string | undefined;
                    referral_name?: string | undefined;
                    query?: string | undefined;
                };
                reason?: string | undefined;
            }[];
        };
        requiresPatient: true;
        requiresDocument: false;
        requiresTemplate: false;
    };
    "Appointment Additional Considerations": {
        input: {
            $$$HR: string;
            appointment_note: string;
            transcription: string;
            current_actions: string;
        };
        output: {
            deltas: {
                change: "add" | "remove" | "update";
                action_id?: string | undefined;
                action?: {
                    type: "additional-consideration";
                    description: string;
                    consideration: string;
                    category: "medication_issue" | "diagnostic" | "alternative_plan" | "red_flag" | "safety";
                    priority: "high" | "medium" | "low";
                    status: "ready" | "in-progress" | "pending-approval" | "done" | "rejected" | "error";
                    rationale: string;
                } | undefined;
                reason?: string | undefined;
            }[];
        };
        requiresPatient: true;
        requiresDocument: false;
        requiresTemplate: false;
    };
    "Appointment Analysis": {
        input: {
            appointment: string;
            $$$HR: string;
        };
        output: {
            translated_concern?: string | undefined;
            briefing: string;
            tldr: string;
        };
        requiresPatient: true;
        requiresDocument: false;
        requiresTemplate: false;
    };
    "Appointment Concern Prompt": {
        input: {
            prompt: string;
            appointment: string;
        };
        output: {
            result: string;
        };
        requiresPatient: true;
        requiresDocument: false;
        requiresTemplate: false;
    };
    "Beautify Note": {
        input: {
            note: string;
            extraPrompt?: string | undefined;
        };
        output: string;
        requiresPatient: false;
        requiresDocument: false;
        requiresTemplate: false;
    };
    "Carey Assistant": {
        input: {};
        output: string;
        requiresPatient: false;
        requiresDocument: false;
        requiresTemplate: false;
    };
    "Carey Conversation Classifier": {
        input: {
            transcript: string;
            facts: string;
        };
        output: {
            intents: ("booking" | "reschedule" | "cancel" | "refill" | "pharmacy_change" | "records_lookup" | "results_lookup" | "document_send" | "status_check" | "relay_message" | "pricing_faq" | "clinic_info" | "account_membership" | "waitlist" | "operational" | "other")[];
            outcome: "resolved_autonomous" | "resolved_designed_handoff" | "handoff_capability_gap" | "handoff_unnecessary" | "human_first" | "abandoned" | "failed";
            handoff_initiator: "none" | "patient_first_message" | "patient_after_attempt" | "carey_offered_patient_accepted" | "carey_immediate";
            flags: {
                wrong_info: boolean;
                error_fallback_shown: boolean;
                patient_frustration: boolean;
                repeated_request: boolean;
                missed_question: boolean;
            };
            missing_capability: string | null;
            reason: string;
        };
        requiresPatient: false;
        requiresDocument: false;
        requiresTemplate: false;
    };
    "Carey Handoff Summary": {
        input: {
            transcript: string;
            reason?: string | undefined;
        };
        output: string;
        requiresPatient: false;
        requiresDocument: false;
        requiresTemplate: false;
    };
    "Chat Reply Suggestions": {
        input: {
            chat: string;
        };
        output: string[];
        requiresPatient: false;
        requiresDocument: false;
        requiresTemplate: false;
    };
    "Chat Subject Generation": {
        input: {
            firstMessage: string;
        };
        output: string;
        requiresPatient: false;
        requiresDocument: false;
        requiresTemplate: false;
    };
    "Document Analysis Batch": {
        input: {
            $$$HR: string;
        };
        output: {
            analysis: string;
            tldr: string;
        };
        requiresPatient: true;
        requiresDocument: true;
        requiresTemplate: false;
    };
    "Document Analysis": {
        input: {
            $$$HR: string;
        };
        output: string;
        requiresPatient: true;
        requiresDocument: true;
        requiresTemplate: false;
    };
    "Drug Allergy Checker v2": {
        input: {
            drugs: string;
            allergies: string;
        };
        output: {
            drug_id: string;
            allergy_name: string | null;
            matched: boolean;
        }[];
        requiresPatient: false;
        requiresDocument: false;
        requiresTemplate: false;
    };
    "Fill Long Referral Text": {
        input: {
            $$$HR: string;
            appointmentNote: string;
            transcription: string;
            contact: string;
        };
        output: {
            reason_for_referral: string;
            further_discussion: string;
        };
        requiresPatient: true;
        requiresDocument: false;
        requiresTemplate: false;
    };
    "Generate Document Template Suggestions": {
        input: {
            note: string;
            appointment: string;
            appointmentTranscription: string;
            previousDocuments: string;
        };
        output: {
            suggestedTemplateIds: string[];
        };
        requiresPatient: false;
        requiresDocument: false;
        requiresTemplate: false;
    };
    "Generate Prescription": {
        input: {
            note: string;
            appointment: string;
            prescriptions: string;
            allergies: string;
            transcription: string;
            favoriteMedications: string;
            additionalInstructions: string;
        };
        output: string;
        requiresPatient: true;
        requiresDocument: false;
        requiresTemplate: false;
    };
    "Generate Query For Document Search": {
        input: {
            $$$HR: string;
            note: string;
            transcription: string;
        };
        output: string[];
        requiresPatient: true;
        requiresDocument: false;
        requiresTemplate: false;
    };
    "Generate Template Description For Embedding": {
        input: {
            description: string;
            category: string;
            subcategories: string;
            field_descriptions: string;
        };
        output: string;
        requiresPatient: false;
        requiresDocument: false;
        requiresTemplate: true;
    };
    "Helpdesk Assistant Reply": {
        input: {
            type: string;
            guidance: string;
            messages: string;
        };
        output: string;
        requiresPatient: false;
        requiresDocument: false;
        requiresTemplate: false;
    };
    "Immunization Transcription": {
        input: {
            vaccines: string;
        };
        output: {
            ocr_name?: string | undefined;
            name: string;
            description?: string | undefined;
            prescription?: string | undefined;
            given_date?: string | undefined;
            expiry_date?: string | undefined;
            lot?: string | undefined;
            dosage?: string | undefined;
            site?: string | undefined;
            ocr_route?: string | undefined;
            route?: string | undefined;
        }[];
        requiresPatient: true;
        requiresDocument: true;
        requiresTemplate: false;
    };
    "Main Context": {
        input: {};
        output: string;
        requiresPatient: false;
        requiresDocument: false;
        requiresTemplate: false;
    };
    "Medication Cross-taper": {
        input: {
            inputs: string;
        };
        output: string;
        requiresPatient: false;
        requiresDocument: false;
        requiresTemplate: false;
    };
    "Note P Section Suggestions": {
        input: {
            $$$DATE: string;
            note: string;
            records: string;
        };
        output: string;
        requiresPatient: false;
        requiresDocument: false;
        requiresTemplate: false;
    };
    "Note Stamp Suggestion from Transcription": {
        input: {
            transcription: string;
            templates: string;
        };
        output: string;
        requiresPatient: false;
        requiresDocument: false;
        requiresTemplate: false;
    };
    "Note Summary": {
        input: {
            note: string;
        };
        output: string;
        requiresPatient: false;
        requiresDocument: false;
        requiresTemplate: false;
    };
    "Patient Analysis": {
        input: {
            $$$HR: string;
        };
        output: string;
        requiresPatient: true;
        requiresDocument: false;
        requiresTemplate: false;
    };
    "Patient Records Chat": {
        input: {
            prompt: string;
        };
        output: string;
        requiresPatient: true;
        requiresDocument: false;
        requiresTemplate: false;
    };
    "Pharmacy Authorization Analysis 2": {
        input: {
            $$$HR_withMedicationIds: string;
        };
        output: {
            pharmacy?: {
                name: string;
                phone?: string | undefined;
                fax?: string | undefined;
                faxReplyTo?: string | undefined;
                email?: string | undefined;
                address?: {
                    line1?: string | undefined;
                    line2?: string | undefined;
                    postcode?: string | undefined;
                    city?: string | undefined;
                    state?: string | undefined;
                    country?: string | undefined;
                } | undefined;
            } | undefined;
            medications_in_document: {
                name: string;
            }[];
            medications_in_hr: {
                name: string;
                medication_id: string;
            }[];
            analysis: {
                medication_name: string;
                analysis: string;
                allow_refill: boolean;
            }[];
        };
        requiresPatient: true;
        requiresDocument: true;
        requiresTemplate: false;
    };
    "Suggest Prefilled Document Templates": {
        input: {
            demographics: string;
            note: string;
            transcription: string;
            prefilledTemplates: string;
            unfilledTemplates: string;
            npPrompt: string;
        };
        output: {
            prefilled: string[];
            unfilled: string[];
        };
        requiresPatient: true;
        requiresDocument: false;
        requiresTemplate: false;
    };
    "Task to Note": {
        input: {
            task: string;
        };
        output: string;
        requiresPatient: false;
        requiresDocument: false;
        requiresTemplate: false;
    };
    "Template Field Extraction": {
        input: {
            metadata: string;
        };
        output: {
            width: number;
            height: number;
            inputs: {
                type: "textfield" | "textarea" | "checkbox" | "signature" | "patient signature";
                x_percent: string;
                y_percent: string;
                w_percent: string;
                h_percent: string;
                id: string;
                placeholder: "empty" | "patient_name" | "patient_first_name" | "patient_middle_name" | "patient_last_name" | "patient_address" | "patient_address_line1" | "patient_address_line1_2" | "patient_address_line2" | "patient_address_postcode" | "patient_address_city" | "patient_address_state" | "patient_address_country" | "patient_gender" | "patient_age" | "patient_age_months" | "patient_birthdate" | "patient_birthdate_ddmmyy" | "patient_birthdate_ddmmyyyy" | "patient_birthdate_day" | "patient_birthdate_month" | "patient_birthdate_year" | "patient_phone" | "patient_email" | "patient_ohip_number" | "patient_ohip_number_only" | "patient_ohip_version_only" | "patient_ohip_number_formatted" | "patient_ohip_number_separated_first" | "patient_ohip_number_separated_second" | "patient_ohip_number_separated_third" | "patient_ohip_expiry" | "practitioner_initials" | "practitioner_name" | "practitioner_first_name" | "practitioner_last_name" | "practitioner_billing" | "practitioner_college_registration" | "practitioner_title" | "practitioner_phone" | "practitioner_clinic" | "practitioner_fax" | "practitioner_email" | "practitioner_address" | "practitioner_address_line1" | "practitioner_address_line1_2" | "practitioner_address_line2" | "practitioner_address_postcode" | "practitioner_address_city" | "practitioner_address_state" | "practitioner_address_country" | "lifelabs_contract" | "date" | "date_no_separator" | "date_ddmmyyyy" | "date_ddmmyyyy_no_separator" | "date_mmddyyyy" | "date_mmddyyyy_no_separator" | "time" | "time_military" | "date_day" | "date_month" | "date_year_yy" | "date_year_yyyy" | "date_empty" | "date_year_empty_yyyy" | "date_year_empty_yy" | "date_month_empty" | "date_day_empty" | "checked_by_default" | "patient_gender_male" | "patient_gender_female" | "patient_ohip_valid" | "patient_no_ohip_valid";
            }[];
        }[];
        requiresPatient: false;
        requiresDocument: false;
        requiresTemplate: true;
    };
    "Template Generator": {
        input: {
            current_template: string;
            available_parameters: string;
            user_instruction: string;
        };
        output: string;
        requiresPatient: false;
        requiresDocument: false;
        requiresTemplate: false;
    };
    "Transcription to Note": {
        input: {
            date: string;
            note: string;
            template: string;
            transcription: string;
            instructions: string;
            appointment: string;
            records: string;
        };
        output: string;
        requiresPatient: false;
        requiresDocument: false;
        requiresTemplate: false;
    };
    "Another Test Prompt": {
        input: {};
        output: string;
        requiresPatient: false;
        requiresDocument: false;
        requiresTemplate: false;
    };
    "Test How Fun": {
        input: {};
        output: string;
        requiresPatient: false;
        requiresDocument: false;
        requiresTemplate: false;
    };
    "Test Prompt": {
        input: {};
        output: string;
        requiresPatient: false;
        requiresDocument: false;
        requiresTemplate: false;
    };
    "Menopause - Executive Report": {
        input: {
            $$$HR: string;
        };
        output: string;
        requiresPatient: true;
        requiresDocument: false;
        requiresTemplate: false;
    };
    "Menopause - Provider Report": {
        input: {
            $$$HR: string;
        };
        output: string;
        requiresPatient: true;
        requiresDocument: false;
        requiresTemplate: false;
    };
    "Peak Performance - Executive Report": {
        input: {
            $$$HR: string;
        };
        output: string;
        requiresPatient: true;
        requiresDocument: false;
        requiresTemplate: false;
    };
    "Precision Health - Executive Summary": {
        input: {
            $$$HR: string;
        };
        output: {
            overview: string;
            objectives: {
                priority: "Primary" | "Secondary" | "Tertiary" | "Quaternary";
                description?: string | undefined;
            }[];
            highlights: string[];
        };
        requiresPatient: true;
        requiresDocument: false;
        requiresTemplate: false;
    };
    "Precision Health - Genetic Insights": {
        input: {
            $$$HR: string;
        };
        output: {
            precisionMedicine?: {
                impacts: {
                    variant: string;
                    description: string;
                }[];
            } | undefined;
            geneticProfile?: {
                variants: {
                    name: string;
                    impacts: {
                        category: string;
                        description: string;
                    }[];
                    recommendations: string[];
                }[];
                generalRecommendations: string[];
                pregnancyConsiderations?: {
                    category: string;
                    description: string;
                }[] | undefined;
            } | undefined;
        };
        requiresPatient: true;
        requiresDocument: false;
        requiresTemplate: false;
    };
    "Precision Health - Health Dashboard": {
        input: {
            $$$HR: string;
        };
        output: string;
        requiresPatient: true;
        requiresDocument: false;
        requiresTemplate: false;
    };
    "Precision Health - Medication Optimization": {
        input: {
            $$$HR: string;
        };
        output: {
            context?: string | undefined;
            targetContext?: string | undefined;
            transitions?: {
                category: string;
                current: string;
                plan?: string[] | undefined;
                goal?: string | undefined;
                recommendation?: string | undefined;
                rationale?: string[] | undefined;
                status?: string | undefined;
            }[] | undefined;
            pregnancyConsiderations?: {
                category: string;
                current: string;
                pregnancyPlan: string;
                safety: string;
                alternatives?: string[] | undefined;
            }[] | undefined;
        };
        requiresPatient: true;
        requiresDocument: false;
        requiresTemplate: false;
    };
    "Precision Health - Monitoring Protocol": {
        input: {
            $$$HR: string;
        };
        output: {
            schedule?: {
                title: string;
                frequencyHeader: string;
                targetHeader: string;
                integrationHeader: string;
                tests: {
                    name: string;
                    frequency: string;
                    target: string;
                    integration: string;
                }[];
            } | undefined;
            partnerIntegration?: {
                title: string;
                sections: {
                    subtitle: string;
                    description: string;
                    items: {
                        category: string;
                        description: string;
                    }[];
                }[];
                recommendations: {
                    title: string;
                    items: string[];
                };
            } | undefined;
            successMetrics?: {
                title: string;
                metrics: {
                    category: string;
                    trackingFormat: string;
                }[];
                instructions: string[];
            } | undefined;
        };
        requiresPatient: true;
        requiresDocument: false;
        requiresTemplate: false;
    };
    "Precision Health - Supplement Details": {
        input: {
            $$$HR: string;
            supplements: string;
        };
        output: {
            supplementGuide?: {
                title: string;
                categories: {
                    name: string;
                    supplements: {
                        name: string;
                        dosage: string;
                        reason: string;
                        benefits: string[];
                        brandRecommendation: string;
                        timing: string;
                        interactions: string;
                    }[];
                }[];
            } | undefined;
            sourcingGuide?: {
                categories: {
                    name: string;
                    products: {
                        supplement: string;
                        brand: string;
                    }[];
                }[];
                costSaving: {
                    strategies: string[];
                    totalCost: string;
                };
            } | undefined;
            troubleshooting?: {
                issues: {
                    title: string;
                    symptoms?: string | undefined;
                    commonCulprits?: string | undefined;
                    solution?: string | undefined;
                    solutions?: string[] | undefined;
                    alternative?: string | undefined;
                    note?: string | undefined;
                    important?: string | undefined;
                    action?: string | undefined;
                }[];
            } | undefined;
            travelProtocol?: {
                sections: {
                    title: string;
                    guidelines: string[];
                }[];
            } | undefined;
        };
        requiresPatient: true;
        requiresDocument: false;
        requiresTemplate: false;
    };
    "Precision Health - Supplement Protocol": {
        input: {
            $$$HR: string;
        };
        output: string;
        requiresPatient: true;
        requiresDocument: false;
        requiresTemplate: false;
    };
    "Precision Health - Timeline": {
        input: {
            $$$HR: string;
        };
        output: any;
        requiresPatient: true;
        requiresDocument: false;
        requiresTemplate: false;
    };
    "Note Extraction - Active Issues": {
        input: {
            existingItems: string;
            note: string;
        };
        output: {
            name: string;
            notes?: string | undefined;
        }[];
        requiresPatient: false;
        requiresDocument: false;
        requiresTemplate: false;
    };
    "Note Extraction - Allergies": {
        input: {
            existingItems: string;
            note: string;
        };
        output: {
            name: string;
            notes?: string | undefined;
        }[];
        requiresPatient: false;
        requiresDocument: false;
        requiresTemplate: false;
    };
    "Note Extraction - History": {
        input: {
            existingItems: string;
            note: string;
            historyType: string;
        };
        output: {
            name: string;
            details?: string | undefined;
        }[];
        requiresPatient: false;
        requiresDocument: false;
        requiresTemplate: false;
    };
    "Note Extraction - Issues": {
        input: {
            existingItems: string;
            note: string;
        };
        output: {
            name: string;
            issue_type: "past" | "current";
        }[];
        requiresPatient: false;
        requiresDocument: false;
        requiresTemplate: false;
    };
    "Note Extraction - Lifestyle": {
        input: {
            note: string;
        };
        output: {
            name: string;
            details?: string | undefined;
        }[];
        requiresPatient: false;
        requiresDocument: false;
        requiresTemplate: false;
    };
    "Note Extraction - Medications": {
        input: {
            existingItems: string;
            note: string;
        };
        output: {
            name: string;
        }[];
        requiresPatient: false;
        requiresDocument: false;
        requiresTemplate: false;
    };
    "Note Extraction - Metrics": {
        input: {
            note: string;
        };
        output: {
            name: "temperature" | "bp/Sitting" | "bp/Standing" | "bp/Supine" | "bp/Average" | "hr/Sitting" | "hr/Standing" | "rr" | "o2" | "glucose" | "height" | "weight" | "a1c" | "frs" | "bmi";
            value: string;
        }[];
        requiresPatient: false;
        requiresDocument: false;
        requiresTemplate: false;
    };
    "Note Extraction - Preventive Screenings": {
        input: {
            note: string;
        };
        output: {
            name: string;
            notes?: string | undefined;
        }[];
        requiresPatient: false;
        requiresDocument: false;
        requiresTemplate: false;
    };
    "Lab Result Comment": {
        input: {
            results: string;
            $$$HR: string;
        };
        output: string;
        requiresPatient: true;
        requiresDocument: false;
        requiresTemplate: false;
    };
    "Lab Result Extraction and Comment": {
        input: {
            $$$HR: string;
        };
        output: {
            date_service?: string | undefined;
            results_text: {
                test: string;
                flag: string;
                result: string;
                reference_units: string;
                date?: string | undefined;
                result_type?: string | undefined;
                abnormal?: string | undefined;
            }[];
            comment: string;
        };
        requiresPatient: true;
        requiresDocument: false;
        requiresTemplate: false;
    };
    "Lab Result Extraction": {
        input: {};
        output: {
            date_service?: string | undefined;
            results_text: {
                test: string;
                flag: string;
                result: string;
                reference_units: string;
                date?: string | undefined;
                result_type?: string | undefined;
                abnormal?: string | undefined;
            }[];
            red_flags?: string[] | undefined;
        };
        requiresPatient: true;
        requiresDocument: true;
        requiresTemplate: false;
    };
    "Lab Result Text Result Extractor": {
        input: {
            result: string;
        };
        output: {
            test: string;
            result: string;
            flag: string;
            result_type?: string | undefined;
        };
        requiresPatient: false;
        requiresDocument: false;
        requiresTemplate: false;
    };
    "Executive Report - Annual Physical": {
        input: {
            $$$HR: string;
        };
        output: string;
        requiresPatient: true;
        requiresDocument: false;
        requiresTemplate: false;
    };
    "Executive Report - Health Insights": {
        input: {
            $$$HR: string;
        };
        output: {
            providerNote: string;
            recommendations: {
                icon: string;
                title: string;
                description: string;
            }[];
            focusArea?: {
                title: string;
                description: string;
                details: {
                    subtitle: string;
                    bulletTitle: string;
                    bulletPoints: string[];
                    tip: string;
                };
            } | undefined;
        };
        requiresPatient: true;
        requiresDocument: false;
        requiresTemplate: false;
    };
    "Executive Report - Health Metrics": {
        input: {
            $$$HR: string;
        };
        output: {
            metrics: {
                name: string;
                category: string;
                visualizationType: "lineWithTarget";
                chartData: {
                    name: string;
                    unit: string;
                    target: number;
                    targetDirection: "higher" | "lower";
                    data: {
                        date: string;
                        value: number;
                    }[];
                };
                interpretation: string;
            }[];
            labResults: {
                name: string;
                value: string;
                unit: string;
                referenceRange: string;
                date: string;
                status: "normal" | "high" | "low";
            }[];
        };
        requiresPatient: true;
        requiresDocument: false;
        requiresTemplate: false;
    };
    "Executive Report - Medication History": {
        input: {
            $$$HR: string;
        };
        output: {
            medicationTimeline: {
                date: string;
                title: string;
                description: string;
                type: "start" | "change" | "stop";
            }[];
            currentMedications: {
                name: string;
                dose: string;
                schedule: string;
                purpose: string;
                prescriber: string;
            }[];
            medicationNotes: {
                description: string;
                points: string[];
            };
        };
        requiresPatient: true;
        requiresDocument: false;
        requiresTemplate: false;
    };
    "Executive Report - Nutritional Lifestyle": {
        input: {
            $$$HR: string;
        };
        output: {
            conditions: string[];
            recommendations: string[];
            progressMetrics: {
                name: string;
                icon: string;
                percentage: number;
                label: string;
                current: string;
                goal: string;
            }[];
            healthImpact: {
                description: string;
                points: [
                    string,
                    string,
                    string
                ];
            };
        };
        requiresPatient: true;
        requiresDocument: false;
        requiresTemplate: false;
    };
    "Executive Report - Overview": {
        input: {
            $$$HR: string;
        };
        output: {
            highlights: {
                type: "management" | "action" | "standard" | "improvement" | "alert" | "preventive" | "monitoring" | "education" | "goal";
                icon: string;
                title: string;
                description: string;
            }[];
        };
        requiresPatient: true;
        requiresDocument: false;
        requiresTemplate: false;
    };
    "Executive Report - Risk Factors": {
        input: {
            $$$HR: string;
        };
        output: {
            riskFactors?: {
                name: string;
                riskLevel: "High" | "Moderate" | "Low";
                description: string;
                actionItems: string[];
            }[] | undefined;
            riskSummary?: {
                icon: string;
                description: string;
            } | undefined;
            geneticSummary?: string | undefined;
            familyConditions?: {
                name: string;
            }[] | undefined;
            familyHistory?: {
                relation: string;
                conditions: string[];
                notes?: string | undefined;
            }[] | undefined;
            familyImplications?: {
                description: string;
                recommendations: string[];
            } | undefined;
            preventiveRecommendations?: {
                title: string;
                icon: string;
                description: string;
                frequency: string;
                nextDue: string;
            }[] | undefined;
        };
        requiresPatient: true;
        requiresDocument: false;
        requiresTemplate: false;
    };
    "Documentation Revision": {
        input: {
            title: string;
            article: string;
            request: string;
        };
        output: string;
        requiresPatient: false;
        requiresDocument: false;
        requiresTemplate: false;
    };
    "Documentation Tags": {
        input: {
            title: string;
            article: string;
        };
        output: string[];
        requiresPatient: false;
        requiresDocument: false;
        requiresTemplate: false;
    };
}

export const jsonPrompts = {
  "Appointment Action Suggestions": true,
  "Appointment Additional Considerations": true,
  "Appointment Analysis": true,
  "Appointment Concern Prompt": true,
  "Carey Conversation Classifier": true,
  "Chat Reply Suggestions": true,
  "Document Analysis Batch": true,
  "Drug Allergy Checker v2": true,
  "Fill Long Referral Text": true,
  "Generate Document Template Suggestions": true,
  "Generate Query For Document Search": true,
  "Immunization Transcription": true,
  "Pharmacy Authorization Analysis 2": true,
  "Suggest Prefilled Document Templates": true,
  "Template Field Extraction": true,
  "Precision Health - Executive Summary": true,
  "Precision Health - Genetic Insights": true,
  "Precision Health - Medication Optimization": true,
  "Precision Health - Monitoring Protocol": true,
  "Precision Health - Supplement Details": true,
  "Note Extraction - Active Issues": true,
  "Note Extraction - Allergies": true,
  "Note Extraction - History": true,
  "Note Extraction - Issues": true,
  "Note Extraction - Lifestyle": true,
  "Note Extraction - Medications": true,
  "Note Extraction - Metrics": true,
  "Note Extraction - Preventive Screenings": true,
  "Lab Result Extraction and Comment": true,
  "Lab Result Extraction": true,
  "Lab Result Text Result Extractor": true,
  "Executive Report - Health Insights": true,
  "Executive Report - Health Metrics": true,
  "Executive Report - Medication History": true,
  "Executive Report - Nutritional Lifestyle": true,
  "Executive Report - Overview": true,
  "Executive Report - Risk Factors": true,
  "Documentation Tags": true
} as const
